import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, KeyRound, MessageCircleQuestion, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";

export default function ForgotPassword() {
    const { forgotPassword } = useAuth();
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const [emailSent, setEmailSent] = useState(true);
    const [fallbackUrl, setFallbackUrl] = useState("");
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const data = await forgotPassword(email);
            setSent(true);
            setEmailSent(data?.email_sent !== false);
            if (data?.reset_url) setFallbackUrl(data.reset_url);
        } catch (err) {
            setError(apiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-[calc(100vh-4rem)] flex items-start justify-center bg-slate-50 px-4 py-14 sm:py-20">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <Link to="/" className="inline-flex items-center hover:opacity-80">
                        <img src="/brand-header.png" alt="CondominioNow" className="h-[60px] w-auto object-contain" />
                    </Link>
                </div>
                <Card className="border-slate-200 shadow-sm">
                    <CardHeader className="space-y-1 pb-6">
                        <div className="size-10 rounded-xl bg-primary/10 flex items-center justify-center mb-2">
                            <KeyRound className="size-5 text-primary" />
                        </div>
                        <h1 className="font-serif text-3xl font-semibold">Recupera password</h1>
                        <p className="text-sm text-muted-foreground">
                            Inserisci l'email registrata: ti manderemo un link per reimpostare la
                            password.
                        </p>
                    </CardHeader>
                    <CardContent>
                        {sent ? (
                            <div className="space-y-4" data-testid="forgot-sent">
                                {emailSent && !fallbackUrl ? (
                                    <Alert className="border-emerald-300 bg-emerald-50 text-emerald-900">
                                        <CheckCircle2 className="size-4" />
                                        <AlertDescription>
                                            <div className="font-semibold">Email inviata</div>
                                            <div className="text-sm mt-1">
                                                Se l'email è registrata, riceverai un link per
                                                reimpostare la password. Controlla anche la
                                                cartella <strong>spam</strong>. Il link è valido 60
                                                minuti.
                                            </div>
                                        </AlertDescription>
                                    </Alert>
                                ) : (
                                    <Alert
                                        className="border-amber-300 bg-amber-50 text-amber-900"
                                        data-testid="forgot-fallback-alert"
                                    >
                                        <KeyRound className="size-4" />
                                        <AlertDescription>
                                            <div className="font-semibold">
                                                Non siamo riusciti a inviarti l'email
                                            </div>
                                            <div className="text-sm mt-1">
                                                Puoi comunque reimpostare la password cliccando
                                                questo link (valido 60 minuti):
                                            </div>
                                            {fallbackUrl && (
                                                <a
                                                    href={fallbackUrl}
                                                    data-testid="forgot-fallback-link"
                                                    className="mt-2 block break-all text-xs font-semibold text-primary underline"
                                                >
                                                    {fallbackUrl}
                                                </a>
                                            )}
                                        </AlertDescription>
                                    </Alert>
                                )}
                                <Button
                                    asChild
                                    variant="outline"
                                    className="w-full"
                                    data-testid="forgot-back-login"
                                >
                                    <Link to="/login">Torna al login</Link>
                                </Button>
                            </div>
                        ) : (
                            <form
                                onSubmit={submit}
                                className="space-y-4"
                                data-testid="forgot-form"
                                noValidate
                            >
                                {error && (
                                    <Alert variant="destructive">
                                        <AlertDescription>{error}</AlertDescription>
                                    </Alert>
                                )}
                                <div className="space-y-2">
                                    <Label htmlFor="email">Email</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        required
                                        autoComplete="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        data-testid="forgot-email"
                                        placeholder="tuo@email.it"
                                        autoFocus
                                    />
                                </div>
                                <Button
                                    data-testid="forgot-submit"
                                    type="submit"
                                    className="w-full h-11"
                                    disabled={loading || !email}
                                >
                                    {loading && <Loader2 className="size-4 mr-2 animate-spin" />}
                                    Invia link di recupero
                                </Button>
                                <div className="text-center text-sm text-muted-foreground pt-2">
                                    <Link
                                        to="/login"
                                        className="text-primary font-semibold hover:underline"
                                    >
                                        Torna al login
                                    </Link>
                                </div>
                            </form>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
